import React, { useMemo } from 'react';
import { Box, Card, Tag, Text, Title } from '@nimbus-ds/components';
import { InteractiveList } from '@nimbus-ds/patterns';
import { CheckoutPaymentMethod } from '@tiendanube/common/src/enums';
import { ErrorState } from '@tiendanube/components';
import { BILLING_NEW_FLOW_FREQUENCY } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { useToastStatus } from 'commons/hooks';
import { combineStatus } from 'commons/utils/combineStatus';
import {
  usePaymentMethods,
  usePayorderToPay,
} from 'domains/Billing/Checkout/hooks';
import useTranslatePaymentMethod from 'domains/Billing/Checkout/hooks/useTranslatePaymentMethod';
import {
  getIconForPaymentMethod,
  paymentMethodIsMercadoPago,
} from 'domains/Billing/Checkout/pages/utils';
import { trackingBillingSelectPaymentMethodForPlanClick } from 'domains/Billing/Checkout/tracking';
import { useFormatCurrency } from 'domains/Billing/hooks';
import useTranslationBilling from 'domains/Billing/useTranslationBilling';
import NECheckoutCommunication from 'domains/NuvemEnvioCommons/components/CheckoutCommunication';
import {
  NE_POST_PAID_SHIPPING_CONCEPT_CODES,
  NE_TYPE_OF_PAYMENT_PROMOTION,
} from 'domains/NuvemEnvioCommons/constants';
import ChoosePaymentMethodSkeleton from './Skeleton';
import { useContinuePayment } from '../../../ChoosePaymentRecurrencyPage/hooks';

export default function ChoosePaymentMethod() {
  const t = useTranslationBilling('checkout.choosePaymentMethodWithRecurrency');
  const {
    payOrderId,
    recurrentPaymentCompatible,
    status: payorderStatus,
    payorderToPay,
    isForCurrentPlan,
    feePercentageForMethod,
    mainConcept,
    feesForMethod,
    totalFeeForMethodWithTaxes,
    totalWithoutFees,
    conceptsToPay,
  } = usePayorderToPay();

  const [hasSkipRecurrenciesTag] = useHasTags(BILLING_NEW_FLOW_FREQUENCY);

  const {
    refreshPaymentMethods,
    paymentMethods: paymentMethodsForPlan,
    status: paymentMethodsStatus,
  } = usePaymentMethods(isForCurrentPlan);
  const { isLoading, isError } = combineStatus(
    payorderStatus,
    isForCurrentPlan ? paymentMethodsStatus : undefined,
  );
  const paymentMethods = payorderToPay?.allowedPaymentMethods;
  const {
    onPay,
    status: continueStatus,
    goToRecurrencies,
  } = useContinuePayment();
  const { translateCheckoutPaymentMethod } = useTranslatePaymentMethod();
  const formatCurrency = useFormatCurrency();

  useToastStatus({
    status: continueStatus,
    error: t('chooseMethod.continueError'),
    progress: t('chooseMethod.continueLoading'),
  });

  const paymentExcludingFeesNuvemEnvio = useMemo(() => {
    const hasPrePaidShippingCredit = conceptsToPay?.has('shipping-credit');

    const hasPostPaidShippingCharge = conceptsToPay
      ? Array.from(conceptsToPay).some((item) =>
          NE_POST_PAID_SHIPPING_CONCEPT_CODES.has(item),
        )
      : false;

    const mpBankTransferFee = payorderToPay?.paymentFees?.find(
      (fee) => fee.method === CheckoutPaymentMethod.MP_BANK_TRANSFER,
    );

    if (mpBankTransferFee) {
      return null;
    }

    if (hasPrePaidShippingCredit) {
      return NE_TYPE_OF_PAYMENT_PROMOTION.PRE_PAID;
    }

    if (hasPostPaidShippingCharge) {
      return NE_TYPE_OF_PAYMENT_PROMOTION.POST_PAID;
    }
  }, [payorderToPay, conceptsToPay]);

  if (isLoading) return <ChoosePaymentMethodSkeleton />;
  if (isError)
    return (
      <ErrorState
        title={t('error.title')}
        action={{
          children: t('error.retry'),
          onClick: refreshPaymentMethods,
        }}
      />
    );

  const handleSelectMethod = (selectedMethod: CheckoutPaymentMethod) => {
    const logClick = (recurrenciesAvailable: boolean) =>
      trackingBillingSelectPaymentMethodForPlanClick(
        selectedMethod,
        mainConcept,
        recurrenciesAvailable,
      );

    if (isForCurrentPlan && !hasSkipRecurrenciesTag) {
      const recurrencies = paymentMethodsForPlan?.find(
        ({ method }) =>
          method === selectedMethod ||
          (paymentMethodIsMercadoPago(selectedMethod) && method === 'ticket'),
      )?.recurrencyOptions;
      if (recurrencies?.length === 1) {
        logClick(false);
        onPay(selectedMethod, recurrencies[0]);
      } else {
        logClick(true);
        goToRecurrencies(payOrderId, selectedMethod);
      }
    } else {
      logClick(false);
      onPay(selectedMethod);
    }
  };

  return (
    <>
      {paymentExcludingFeesNuvemEnvio &&
        paymentExcludingFeesNuvemEnvio !== null && (
          <NECheckoutCommunication type={paymentExcludingFeesNuvemEnvio} />
        )}

      <Card padding="none">
        <Box display="flex" flexDirection="column" marginBottom="1-5">
          <Box padding="4">
            <Title as="h4">{t('chooseMethod.title')}</Title>
          </Box>
          <InteractiveList bottomDivider={false}>
            {paymentMethods?.map((method) => {
              const icon = getIconForPaymentMethod(method);
              const feePercentage = feePercentageForMethod(method);
              const totalWithFee =
                (totalWithoutFees ?? 0) +
                (totalFeeForMethodWithTaxes(method) ?? 0);
              const includesIva = feesForMethod(method)?.some(
                ({ includesIva }) => includesIva,
              );
              return (
                <InteractiveList.ButtonItem
                  title=""
                  showTitle={false}
                  key={method}
                  iconButton={{
                    onClick: () => handleSelectMethod(method),
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection="row"
                    gap="3"
                    alignItems="center"
                  >
                    {icon && React.createElement(icon)}
                    <Box display="flex" flexDirection="column" gap="1-5">
                      <Text>
                        {translateCheckoutPaymentMethod(
                          method,
                          recurrentPaymentCompatible,
                        )}
                      </Text>
                      {paymentExcludingFeesNuvemEnvio &&
                        method === CheckoutPaymentMethod.MP_BANK_TRANSFER && (
                          <Tag
                            appearance="success"
                            data-testid="nuvem-envio-tag"
                          >
                            {t('nuvemEnvioTag')}
                          </Tag>
                        )}
                      {!!feePercentage && (
                        <Box display="flex" flexDirection="row" gap="1-5">
                          <Tag appearance="warning">
                            {`${t('extraFee', {
                              percentage: feePercentage,
                            })}${includesIva ? t('includesIva') : ''}`}
                          </Tag>
                          <Text color="neutral-textLow" fontSize="caption">
                            {t('totalWithFee', {
                              total: formatCurrency(totalWithFee),
                            })}
                          </Text>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </InteractiveList.ButtonItem>
              );
            })}
          </InteractiveList>
        </Box>
      </Card>
    </>
  );
}
